<template>
  <BillingFeatureGate feature="technician_earnings"
                      variant="page"
                      :title="$t('billing.features.technician_earnings.pageTitle')"
                      :description="$t('billing.features.technician_earnings.tooltip')">
    <div>
      <Toolbar class="p-mb-4 p-mt-4">
        <template #start>
          <span class="p-mr-3">{{ $t('defaultPercentLabel') }}:</span>
          <InputNumber :locale="computedNumberInputLocale"
                       inputStyle="width: 120px"
                       showButtons
                       @input="changeDefaultPercent($event.value)"
                       id="default_percent"
                       class="p-mr-2"
                       v-model="salarySettings.default_percent"
                       :min="0"
                       :max="100"
                       :minFractionDigits="2"
                       :maxFractionDigits="2"
                       autocomplete="off"/>
          <Button :disabled="employeeIsLoading || !isDataChanged"
                  :loading="employeeIsLoading"
                  @click="saveSalarySettings"
                  class="p-button-raised p-mr-2"
                  icon="ti-save"
                  :label="$t('Save')"/>
          <Button :disabled="employeeIsLoading"
                  :loading="employeeIsLoading"
                  @click="confirmClearSettings"
                  class="p-button-warning p-button-raised"
                  :class="{'p-mr-3': currentLocationName}"
                  icon="ti-close"
                  :label="$t('clearAllSettingsLabel')"/>
        </template>
        <template #end>
          <SearchInput :disabled="dataTableIsLoading"
                       @change-processed-search-value="changeProcessedSearchValue"
                       v-model="searchData"
                       @show-spinner="isLoading = true"/>
        </template>
      </Toolbar>

      <div v-if="currentLocationName" class="p-d-flex p-ai-center p-text-secondary p-mb-2 small-text">
        <i class="ti-info-alt p-mr-1"/>
        {{ $t('salarySettingsLocationInfo') }}: {{ currentLocationName }}
      </div>

      <Spinner v-if="isLoading"/>
      <DesktopDataTable v-else
                        :dataTableIsLoading="dataTableIsLoading"
                        :itemsArrayIsEmpty="itemsArrayIsEmpty"
                        :sort="sort"
                        :showSearchInput="false"
                        :showButtons="false"
                        @sort-items="sortItems"
                        :headers="[
                        {name: 'name', title: $t('Name'), sortable: true},
                        {name: 'time', title: $t('Time'), sortable: true},
                        {name: 'sell', title: $t('sellingPriceShortLabel'), sortable: false},
                        {name: 'percent', title: $t('Percent'), width: '200px', sortable: false},
                        {name: 'value', title: $t('Amount'), width: '200px', sortable: false},
                    ]">
        <template v-slot:body>
          <template v-for="item of items" :key="item.id">
            <tr :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span v-if="item.name">{{ item.name }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.time && +item.time !== 0">{{ formatHoursAndMinutes(item.time) }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
            <span v-if="item.sell !== null && item.sell !== undefined">
              {{ formatCurrency(item.sell, 2, 4) }}
            </span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <InputNumber :locale="computedNumberInputLocale"
                             inputStyle="width: 120px"
                             showButtons
                             @input="changePercent(item, $event.value)"
                             mode="decimal"
                             :min="0"
                             :max="100"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             v-model="item.percent"
                             autocomplete="off"/>
              </td>
              <td>
                <InputNumber :locale="computedNumberInputLocale"
                             inputStyle="width: 120px"
                             showButtons
                             @input="changeValue(item, $event.value)"
                             mode="decimal"
                             :min="0"
                             :max="Number(item.sell || 0)"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             v-model="item.value"
                             autocomplete="off"/>
              </td>
            </tr>
          </template>
        </template>
      </DesktopDataTable>

      <OwnPaginator v-show="!itemsArrayIsEmpty"
                    :rows="pagination.perPage"
                    :currentPage="pagination.currentPage - 1"
                    :totalPages="pagination.pageCount"
                    :totalRecords="pagination.totalCount"
                    :rowsPerPageOptions="[5,10,25,50,100,200]"
                    :pageLinkSize="11"/>

      <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

      <ConfirmModal :visible="confirmModal"
                    @confirm="clearSettings"
                    @close="closeConfirmModal"/>

    </div>
  </BillingFeatureGate>
</template>

<script>
import httpClient from '@/services/http.services'
import ConfirmModal from '@/components/ConfirmModal'
import httpMixins from "@/mixins/httpMixins";
import datatableMixins from "@/mixins/datatableMixins";
import formatMixins from "@/mixins/formatMixins";
import settings from "@/settings";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import BillingFeatureGate from '@/components/billing/BillingFeatureGate.vue'

export default {
  mixins: [ httpMixins, datatableMixins, formatMixins, billingFeaturesMixins ],
  components: { ConfirmModal, BillingFeatureGate },
  emits: ['update-items', 'updateItems'],
  props: {
    user: {
      type: Object,
      default: null,
    },
    employeeIsLoading: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      confirmModal: false,
      isDataChanged: false,
      salarySettings: {
        default_percent: null,
        services: []
      },
      fieldsExpand: '',
      apiLink: 'service',
      sort: {
        column: 'id',
        direction: '-',
      },
    }
  },
  watch: {
    billingEntitlementsReady() {
      this.initializeFeaturePage()
    },
    '$route'() {
      const route = this.$route.path.split('/')
      if (this.$route.path === '/login' || route.indexOf('employees') === -1 || route.indexOf('salary-settings') === -1) {
        return false
      }
      this.checkRouteQuery()
      this.getItems()
    },
    '$store.state.firstLayerIsOpened'() {
      if (!this.$store.state.firstLayerIsOpened) {
        if (this.confirmModal) {
          this.closeConfirmModal()
        }
      }
    }
  },
  mounted() {
    this.initializeFeaturePage()
  },
  methods: {
    initializeFeaturePage() {
      if (!this.billingEntitlementsReady || !this.canUseTechnicianEarnings) return
      if (!this.$route.params.uid) return
      this.checkRouteQuery()
      this.$store.commit('SET_FETCH_IN_FLIGHT', false)
      this.getPageData()
    },
    getPageData() {
      if (!this.canUseTechnicianEarnings) return
      if (!this.user) return
      if (!this.userCanGetData || this.fetchInFlight) return

      const locationSettings = this.getSalarySettingsForCurrentLocation(
          this.user?.employee?.salary_settings
      )

      this.salarySettings = {
        default_percent: this.normalizeNumber(locationSettings.default_percent),
        services: Array.isArray(locationSettings.services)
            ? locationSettings.services.map(service => ({
              id: service.id,
              percent: this.normalizeNumber(service.percent)
            }))
            : []
      }


      this.getItems()
    },
    changeDefaultPercent(value) {
      this.isDataChanged = true
      this.salarySettings.default_percent = this.normalizeNumber(value || 0)

      this.items.forEach(item => {
        const hasCustomPercent = this.salarySettings.services?.some(service => Number(service.id) === Number(item.id))

        if (!hasCustomPercent) {
          item.percent = this.salarySettings.default_percent
          item.value = this.normalizeNumber(Number(item.sell || 0) * (item.percent / 100))
        }
      })
    },
    confirmClearSettings() {
      this.confirmModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeConfirmModal() {
      this.confirmModal = false
      this.$store.commit('toggleFirstLayer', false)
    },
    getSalarySettingsForCurrentLocation(rawSettings) {
      const fallback = {
        default_percent: 0,
        services: [],
      }

      if (!rawSettings) return fallback

      let parsed = rawSettings

      if (typeof rawSettings === 'string') {
        try {
          parsed = JSON.parse(rawSettings)
        } catch (e) {
          return fallback
        }
      }

      const locationId = String(this.currentLocationId || '')

      if (parsed?.by_location && locationId && parsed.by_location[locationId]) {
        return parsed.by_location[locationId]
      }

      return fallback
    },
    async saveSalarySettings() {
      const employeeId = this.user?.employee?.id
      if (!employeeId || !this.currentLocationId) return

      const defaultPercent = this.normalizeNumber(this.salarySettings.default_percent)

      const normalizedSettings = {
        default_percent: defaultPercent,
        services: (this.salarySettings.services || [])
            .filter(service => this.normalizeNumber(service.percent) !== defaultPercent)
            .map(service => ({
              id: Number(service.id),
              percent: this.normalizeNumber(service.percent)
            }))
      }

      const obj = {
        employeeId,
        locationId: this.currentLocationId,
        settings: JSON.stringify(normalizedSettings)
      }

      try {
        const { status, data } = await httpClient.post('employee/save-salary-settings', obj)
        if (status === 200 && data?.success) {
          this.salarySettings = normalizedSettings
          this.$toast.add({severity:'success', detail: this.$t('Data updated'), life: settings.toastLife})
          this.getItems()
          this.isDataChanged = false
        }
      } catch(err) {
        this.showError(err)
      }
    },
    normalizeNumber(value) {
      return this.formatDecimal(value, false)
    },
    upsertSalaryService(item) {
      const defaultPercent = this.normalizeNumber(this.salarySettings.default_percent)
      const itemPercent = this.normalizeNumber(item.percent)

      if (!Array.isArray(this.salarySettings.services)) {
        this.salarySettings.services = []
      }

      // const index = this.salarySettings.services.findIndex(service => service.id === item.id)
      const index = this.salarySettings.services.findIndex(service => Number(service.id) === Number(item.id))

      if (itemPercent === defaultPercent) {
        if (index !== -1) {
          this.salarySettings.services.splice(index, 1)
        }
        return
      }

      const payload = {
        id: Number(item.id),
        percent: itemPercent
      }

      if (index === -1) {
        this.salarySettings.services.push(payload)
      } else {
        this.salarySettings.services[index] = payload
      }
    },

    clearSettings() {
      this.salarySettings.default_percent = 0
      this.salarySettings.services = []

      this.items.forEach(item => {
        item.percent = 0
        item.value = 0
      })

      this.closeConfirmModal()
      this.saveSalarySettings()
    },

    changePercent(item, value) {
      this.isDataChanged = true

      const itemSellPrice = this.normalizeNumber(item.sell || 0)
      const safeValue = Number(value || 0)

      if (safeValue > 100) {
        item.percent = 100
      } else if (safeValue < 0) {
        item.percent = 0
      } else {
        item.percent = this.normalizeNumber(safeValue)
      }

      item.value = this.normalizeNumber(itemSellPrice * (item.percent / 100))

      this.upsertSalaryService(item)
    },

    changeValue(item, value) {
      this.isDataChanged = true

      const itemSellPrice = this.normalizeNumber(item.sell || 0)
      const safeValue = Number(value || 0)

      if (safeValue > itemSellPrice) {
        item.value = itemSellPrice
        item.percent = 100
      } else if (safeValue < 0) {
        item.value = 0
        item.percent = 0
      } else {
        item.value = this.normalizeNumber(safeValue)
        item.percent = itemSellPrice > 0
            ? this.normalizeNumber((item.value * 100) / itemSellPrice)
            : 0
      }

      this.upsertSalaryService(item)
    },

    async getItems() {
      if (!this.canUseTechnicianEarnings) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      this.disableEditButton = true
      const ctrl = this.setController?.('employeeSalarySettings')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data.map(service => {
            // const foundService = this.salarySettings.services?.find(serv => serv.id === service.id)
            const foundService = this.salarySettings.services?.find(
                serv => Number(serv.id) === Number(service.id)
            )
            const computedPercent = foundService
                ? this.normalizeNumber(foundService.percent)
                : this.normalizeNumber(this.salarySettings.default_percent)

            const computedValue = this.normalizeNumber(Number(service.sell || 0) * (computedPercent / 100))

            return {
              id: Number(service.id),
              code: service.code,
              name: service.name,
              time: service.time,
              sell: this.normalizeNumber(service.sell),
              percent: computedPercent,
              value: computedValue,
            }
          })

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        if (ctrl && this._abortors?.employeeSalarySettings === ctrl) {
          this._abortors.employeeSalarySettings = null
        }
      }
    },
  },
  computed: {
    currentLocationId() {
      return Number(this.$store.getters.locationData?.id || 0)
    },
    currentLocationName() {
      if (!this.currentLocationId) return ''

      const location = this.$store.getters.locationData || {}

      return location.name ||
          location.location_name ||
          location.title ||
          `ID ${this.currentLocationId}`
    },
  }
}
</script>

<style scoped lang="scss">
.datatable:not(:first-child) {
  margin-bottom: 45px;
}

.opened-order-data {
  font-size: 0.92em;
}

.table-opened-info__header {
  padding: 8px 10px;
}

.table-opened-info__data {
  padding: 8px !important;
}

.table-opened-info__data--no-border {
  border-bottom: none !important;
}

.order-name-link {
  padding: 2px 3px;
  margin-left: -4px;
}

.order-name-title {
  padding: 6px 4px;
}
.order-name__icon {
  padding: 4px 6px;
  font-size: 0.95em;
  &:nth-child(2) {
    //margin-left: 3px;
  }
  &:hover {
    opacity: 0.70;
  }
}
</style>